import { useState, useRef, useEffect } from 'react';
import { Link, Icon } from 'ui/components';
import IconChevronRight from 'ui/icons/icon-global-chevron-right.svg';
import classNames from 'classnames';
import { useIsDesktop } from 'application/hooks/useIsDesktop';
import styles from './NavigationGlobal.module.scss';

export interface NavigationGlobalProps {
	className?: string;
	heading?: string;
	navGroups?: Models.NavGroupItem[];
}

export const NavigationGlobal: React.FC<NavigationGlobalProps> = ({ className, heading, navGroups }) => {
	const isDesktop = useIsDesktop();
	const [openGroupIndex, setOpenGroupIndex] = useState<number | null>(null);
	const subNavRefs = useRef<(HTMLUListElement | null)[]>([]);
	const subPanelRefs = useRef<(HTMLDivElement | null)[]>([]);
	const navListRef = useRef<HTMLUListElement>(null);
	const [navHeight, setNavHeight] = useState<number | undefined>(undefined);
	const cachedNavListHeight = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (isDesktop) return;
		if (openGroupIndex !== null) {
			setNavHeight(subPanelRefs.current[openGroupIndex]?.scrollHeight);
		} else {
			if (cachedNavListHeight.current === undefined) {
				cachedNavListHeight.current = navListRef.current?.scrollHeight;
			}
			setNavHeight(cachedNavListHeight.current);
		}
	}, [openGroupIndex, isDesktop]);

	return (
		<nav
			role="navigation"
			aria-label={heading}
			className={classNames(
				styles.NavigationGlobal,
				isDesktop ? styles['NavigationGlobal--desktop'] : styles['NavigationGlobal--mobile'],
				!isDesktop && openGroupIndex !== null && styles['NavigationGlobal--subOpen'],
				className,
			)}
			style={!isDesktop && navHeight !== undefined ? { height: navHeight, transition: 'height 0.3s ease', overflow: 'hidden' } : undefined}
		>
			<ul ref={navListRef} className={styles.NavigationGlobal__navList}>
				{navGroups?.map((group, index) => (
					<li key={index} className={styles.NavigationGlobal__navItem}>
						{group.children ? (
							<div
								className={styles.NavigationGlobal__navItemParent}
								onClick={() => {
									if (openGroupIndex === null) {
										cachedNavListHeight.current = navListRef.current?.scrollHeight;
									}
									setOpenGroupIndex(openGroupIndex === index ? null : index);
								}}
								aria-expanded={openGroupIndex === index}
							>
								<div className={styles.NavigationGlobal__navItemParentInner}>
									<div className={styles.NavigationGlobal__navItemIcon}>
										<div className={styles.NavigationGlobal__navItemIconInner}>
											<Icon className={styles.NavigationGlobal__icon}>
												<IconChevronRight />
											</Icon>
										</div>
									</div>
									<span className={styles.NavigationGlobal__navItemTitle}>{group.title}</span>
								</div>
								<div
									ref={(el) => {
										subPanelRefs.current[index] = el;
									}}
									className={classNames(
										styles.NavigationGlobal__subPanel,
										openGroupIndex === index && styles['NavigationGlobal__subPanel--active'],
									)}
								>
									<button
										className={styles.NavigationGlobal__subPanelBack}
										onClick={(e) => {
											e.stopPropagation();
											setOpenGroupIndex(null);
										}}
									>
										<div className={styles.NavigationGlobal__subPanelBackIcon}>
											<div className={styles.NavigationGlobal__subPanelBackIconInner}>
												<Icon className={styles.NavigationGlobal__icon}>
													<IconChevronRight />
												</Icon>
											</div>
										</div>
										<span className={styles.NavigationGlobal__subPanelBackTitle}>{group.title}</span>
									</button>
									<ul
										ref={(el) => {
											subNavRefs.current[index] = el;
										}}
										onClick={(e) => e.stopPropagation()}
										className={styles.NavigationGlobal__subNavList}
										style={
											isDesktop
												? {
														maxHeight:
															openGroupIndex === index
																? `${subNavRefs.current[index]?.scrollHeight ?? 0}px`
																: '0px',
													}
												: undefined
										}
									>
										{group.children.map((child, childIndex) => (
											<li key={childIndex} className={styles.NavigationGlobal__subNavItem}>
												<Link
													url={child.url || '#'}
													className={styles.NavigationGlobal__subNavItemLink}
													title={child.title}
												>
													<span className={styles.NavigationGlobal__subNavItemTitle}>{child.title}</span>
												</Link>
											</li>
										))}
									</ul>
								</div>
							</div>
						) : (
							<div className={styles.NavigationGlobal__navItemNoChildren}>
								<Link url={group.url || '#'} className={styles.NavigationGlobal__navItemLink} title={group.title}>
									<span className={styles.NavigationGlobal__navItemTitle}>{group.title}</span>
								</Link>
							</div>
						)}
					</li>
				))}
			</ul>
		</nav>
	);
};
